import { JsonUtil } from "./JsonUtil";
import { tryPromise } from "./LangUtil";
import { combineHeaders, combinePath, RestContentType, RestInternalError, RestResponse, } from "./RestClient";
import { handleResponse as handleStreamResponse, StreamResponse } from "./StreamClient";
import { GenUtil } from "./GenUtil";
/**
 * 如果状态码不是 200-299, 会抛出异常 [RestResponseError]
 * 如果 JSON 转 Model 出现错误，会抛出异常 [RestTransformError]
 */
export class GlobalClient {
    constructor(adapter, abortManager = undefined) {
        this.adapter = adapter;
        this.abortManager = abortManager;
    }
    async request(params) {
        const adapter = await (async () => this.adapter)();
        let contentType = adapter.contentType;
        if (params.contentType) {
            contentType = params.contentType;
        }
        let requestInit = {
            method: params.method,
            headers: combineHeaders(adapter.headers, params.headers),
        };
        if (contentType) {
            delete requestInit.headers["Content-Type"];
        }
        if (params.data) {
            let body = params.data;
            if (params.data instanceof FormData) {
                body = params.data;
            }
            else {
                if (contentType == RestContentType.json) {
                    requestInit.headers["Content-Type"] = contentType;
                    if (typeof params.data !== "string") {
                        body = JsonUtil.stringify(params.data);
                    }
                    else {
                        body = params.data;
                    }
                }
                else if (contentType == RestContentType.form) {
                    requestInit.headers["Content-Type"] = contentType;
                    if (typeof params.data !== "string") {
                        body = JsonUtil.stringify(params.data);
                    }
                    else {
                        body = params.data;
                    }
                }
                else if (!contentType) {
                    // temporary use JSON, but we should be able to determine the content type from body later
                    requestInit.headers["Content-Type"] = RestContentType.json;
                    if (typeof params.data !== "string") {
                        body = JsonUtil.stringify(params.data);
                    }
                    else {
                        body = params.data;
                    }
                }
            }
            requestInit = { ...requestInit, body: body };
        }
        let uuid = params.uuid || GenUtil.randomString();
        const abortController = new AbortController();
        if (this.abortManager) {
            this.abortManager.add(uuid, abortController, params);
        }
        requestInit = { ...requestInit, signal: abortController.signal };
        let url = combinePath(params.path, params.queryParameters);
        const res = await tryPromise(fetch(url, requestInit));
        if (isStreamResponse(res.data?.headers)) {
            return handleStreamResponse(res, adapter, () => {
                if (this.abortManager) {
                    this.abortManager.abort({ uuid: uuid });
                }
            }).then(value => {
                return new GlobalResponse(value);
            });
        }
        else {
            if (this.abortManager) {
                this.abortManager.remove(uuid);
            }
            return handleRestResponse(res, adapter).then(value => {
                return new GlobalResponse(value);
            });
        }
    }
}
async function handleRestResponse(result, adapter) {
    if (result.error) {
        const basicError = new RestInternalError(result.error);
        adapter?.errorHandler(basicError);
        throw basicError;
    }
    const res = result.data;
    const response = new RestResponse(res.status, res.statusText, res.headers, res);
    if (!response.isSuccess) {
        adapter?.errorHandler(response);
    }
    return response;
}
export class GlobalResponse {
    constructor(res) {
        this.res = res;
    }
    get isStream() {
        if (this.res instanceof StreamResponse) {
            return true;
        }
        return false;
    }
    get headers() {
        return this.response.headers;
    }
    get status() {
        return this.response.status;
    }
    get message() {
        return this.response.statusText;
    }
    get response() {
        if (this.res instanceof StreamResponse) {
            return this.res.response;
        }
        return this.res.data;
    }
    get stream() {
        if (this.isStream) {
            return this.res;
        }
        return undefined;
    }
    async json() {
        if (this.isStream) {
            return undefined;
        }
        if (!this.response) {
            return undefined;
        }
        return this.response.json();
    }
    async text() {
        if (this.isStream) {
            return undefined;
        }
        if (!this.response) {
            return undefined;
        }
        return this.response.text();
    }
    async blob() {
        if (this.isStream) {
            return undefined;
        }
        if (!this.response) {
            return undefined;
        }
        return this.response.blob();
    }
    get errorDescription() {
        return this.res.errorDescription;
    }
    get isSuccess() {
        return this.status >= 200 && this.status < 300;
    }
    validate() {
        return this.res.validate();
    }
}
function isStreamResponse(headers) {
    if (!headers) {
        return false;
    }
    return headers.get("Content-Type") === "text/event-stream";
}
